import * as React from 'react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import isNil from 'lodash/isNil';

import { NewTableColumnAction, NewTableColumnId, NewTableDefaultDataItem, NewTableField, NewTableFilterValue, NewTableFolder, NewTableHeaderAction, NewTableLoadMetadata, NewTableLoadMetadataFiltering, NewTableLoadMetadataSorting, NewTableProps, NewTableRowAction, NewTableSortingDirection } from '../new-table.types';
import { processColumnActions } from './columns';
import { processHeaderActions } from './header';
import { MessageProps } from '@common/components/MessageBox.types';

enum ActionSource {
  headerCell = 'headerCell',
  header = 'header',
  row = 'row',
}

export const useController = (props: NewTableProps) => {
  const { 
    data,
    columns,
    disableAutoInit,
    loadMetadata,
    loadItemsCount = 45,
    onLoadData,
    columnActions = [],
    columnsManagement,
    headerActions = [],
    selection,
    itemUniqProperty,
    rowActions = [],
    columnsResizing,
    totalItemsCount = 0,
    moduleName
  } = props;

  if (!itemUniqProperty) {
    console.error(`
    
    
    ${moduleName}: NewTable: itemUniqProperty is missed.

    
    `);
  }

  const [currentActions, setCurrentActions] = React.useState<{ 
    source: ActionSource; 
    coords: { x: number; y: number } 
  } | undefined>();
  const [currentContextMenuColumnId, setCurrentContextMenuColumnId] = React.useState<NewTableColumnId | undefined>();
  const [isOpenedFieldSelector, setOpenedFieldSelectorState] = React.useState(false);
  const [areOpenedFilters, setOpenedFiltersState] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [messageProps, setMessageProps] = React.useState<MessageProps>();

  const tableInstance = useReactTable({
    columnResizeMode: 'onEnd',
    data,
    columns,
    getCoreRowModel: getCoreRowModel() as any,
  });

  const DEFAULT_SORTING = React.useMemo(() => {
    return { id: columns[1]?.id!, direction: NewTableSortingDirection.asc };
  }, [columns[1]?.id]);

  const getData = React.useCallback(async (params: NewTableLoadMetadata) => {
    if (!onLoadData) {
      return;
    }

    try {
      setLoading(true);
      await onLoadData(params);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [onLoadData]);

  React.useEffect(() => {
    if (!disableAutoInit && onLoadData) {
      const initialMetadata: NewTableLoadMetadata = {
        filtering: loadMetadata?.filtering,
        sorting: loadMetadata?.sorting || DEFAULT_SORTING,
        pagination: loadMetadata?.pagination || {
          start: 0,
          size: loadItemsCount
        }
      };

      getData(initialMetadata);
    }
  }, []);

  const handleFilter = (filtering: NewTableLoadMetadataFiltering) => {
    getData({
      filtering,
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting || DEFAULT_SORTING,
    });
  };

  const handleSort = React.useCallback((sorting: NewTableLoadMetadataSorting) => {
    getData({
      filtering: loadMetadata?.filtering,
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting,
    });
  }, [getData, loadMetadata, loadItemsCount]);

  const shouldIgnoreLazyLoading = React.useMemo(() => {
		const shouldIgnore = isLoading || data.length === totalItemsCount || data.length > totalItemsCount;

		return shouldIgnore;
	}, [totalItemsCount, data.length, isLoading]);

  const handleLazyLoad = () => {
    if (shouldIgnoreLazyLoading) {
      return;
    }

    getData({
      filtering: loadMetadata?.filtering,
      pagination: {
        start: data.length,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting || DEFAULT_SORTING,
    });
  };

  const handleColumnRemove = React.useCallback(() => {
    if (columnsManagement?.onRemove && !isNil(currentContextMenuColumnId)) {
      columnsManagement.onRemove(currentContextMenuColumnId);
    }
  }, [currentContextMenuColumnId, columnsManagement?.onRemove]);

  const handleColumnAdd = React.useCallback((data: {
    field: NewTableField,
    folder: NewTableFolder;
  }) => {
    if (columnsManagement?.onAdd) { 
      const lastColumnId = columns[columns.length - 1]?.id;

      columnsManagement.onAdd({
        field: data.field,
        folder: data.folder,
        columnId: currentContextMenuColumnId ?? lastColumnId!,
      });
    }
  }, [columnsManagement?.onAdd, columns, currentContextMenuColumnId]);

  const openFieldSelector = React.useCallback(() => setOpenedFieldSelectorState(true), []);
  const closeFieldSelector = React.useCallback(() => setOpenedFieldSelectorState(false), []);

  const openFilters = React.useCallback(() => setOpenedFiltersState(true), []);
  const closeFilters = React.useCallback(() => setOpenedFiltersState(false), []);

  const applyColumnFilters = (filterValue?: NewTableFilterValue) => {
    closeFilters();

    if (isNil(currentContextMenuColumnId)) {
      return;
    }

    const currentFilters = { ...loadMetadata?.filtering?.columns }

    if (filterValue) {
      currentFilters[currentContextMenuColumnId] = filterValue;
    } else {
      delete currentFilters[currentContextMenuColumnId];
    }

    getData({
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting || DEFAULT_SORTING,
      filtering: {
        filter: true,
        columns: currentFilters
      },
    });
  };

  const clearColumnFilter = () => {
    closeFilters();

    if (isNil(currentContextMenuColumnId)) {
      return;
    }

    const currentFilters = { ...loadMetadata?.filtering?.columns }

    delete currentFilters[currentContextMenuColumnId];

    const hasFilters = Object.keys(currentFilters).length > 0;

    getData({
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting || DEFAULT_SORTING,
      filtering: {
        filter: hasFilters,
        columns: currentFilters
      },
    });
  };

  const processedColumnActions = processColumnActions({
    columnActions,
    currentContextMenuColumnId,
    filtering: loadMetadata?.filtering,
    openFieldSelector,
    onColumnRemove: handleColumnRemove,
    openFilters,
    clearFilter: clearColumnFilter,
  });

  const handleHeaderCellContextMenu = React.useCallback((params: { 
    columnId: NewTableColumnId;
    coords: { x: number; y: number } 
  }) => {
    if (processedColumnActions?.length) {
      setCurrentContextMenuColumnId(params.columnId);
      setCurrentActions({
        source: ActionSource.headerCell,
        coords: params.coords,
      });
    }
  }, [processedColumnActions?.length]);

  const processedHeaderActions = processHeaderActions({
    headerActions,
    openFieldSelector,
  });

  const handleHeaderContextMenu = React.useCallback((params: {
    coords: { x: number; y: number } 
  }) => {
    if (processedHeaderActions?.length) {
      setCurrentContextMenuColumnId(undefined);
      setCurrentActions({
        source: ActionSource.header,
        coords: params.coords,
      });
    }
  }, [processedHeaderActions]);

  const closeHeaderActions = React.useCallback(() => {
    setCurrentActions(undefined);
  }, []);

  const getMenuHorizontalPosition = React.useCallback((xClickPosition: number) => {
		const { innerWidth } = window;
		const half = innerWidth / 2;

		if (xClickPosition > half) {
			return {
				right: innerWidth - xClickPosition,
			};
		}

		return {
			left: xClickPosition,
		};
	}, []);

  const handleRowClick = React.useCallback(({ item, e }: { item: NewTableDefaultDataItem; e: React.MouseEvent<HTMLTableRowElement> }) => {
    const alreadySelectedItemsId = selection?.ids || [];
    const selectedItemId = item[itemUniqProperty];

    let newSelectedItems = [selectedItemId];

    if (e.ctrlKey || e.shiftKey) {
      const isAlreadySelected = alreadySelectedItemsId.some((x) => x === selectedItemId);

      newSelectedItems = isAlreadySelected
        ? alreadySelectedItemsId.filter((x) => x !== selectedItemId)
        : alreadySelectedItemsId.concat(selectedItemId);
    }

    if (selection?.onChange) {
      selection.onChange(newSelectedItems);
    }
  }, [selection, itemUniqProperty]);

  const isRowSelected = React.useCallback((item: NewTableDefaultDataItem) => {
    return Boolean(selection?.ids.includes(item[itemUniqProperty]));
  }, [selection?.ids, itemUniqProperty]);

  const handleRowContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableRowElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if ((selection?.ids.length || 0) > 0) {
      setCurrentActions({
        source: ActionSource.row,
        coords: {
          x: e.clientX,
          y: e.clientY,
        },
      });
    }
  }, [selection?.ids.length]);

  const handleColumnSizeChange = () => {
    if (columnsResizing?.onChange) {
      const columnsSize = tableInstance.getAllColumns().reduce<Record<NewTableColumnId, number>>((acc, col) => {
        acc[col.id] = col.getSize();
  
        return acc;
      }, {});

      columnsResizing?.onChange(columnsSize);
    }
  };

  const actionsConfig: Record<ActionSource, Array<NewTableColumnAction | NewTableHeaderAction | NewTableRowAction>> = {
    [ActionSource.header]: processedHeaderActions,
    [ActionSource.headerCell]: processedColumnActions,
    [ActionSource.row]: rowActions,
  };

  const shouldHaveRowClickHandler = Boolean(selection);
  const currentColumn = columns.find((x) => x.id === currentContextMenuColumnId);

  const closeMessage = React.useCallback(() => {
    setMessageProps(undefined);
  }, []);

  const showMessage = React.useCallback((props: Omit<MessageProps, 'show'>) => {
    setMessageProps({
      ...props,
      show: true,
      onCustomOk: closeMessage,
    });
  }, [closeMessage]);

  return {
    tableInstance,
    currentActions,
    currentContextMenuColumnId,
    processedColumnActions,
    isOpenedFieldSelector,
    processedHeaderActions,
    areOpenedFilters,
    actions: currentActions?.source ? actionsConfig[currentActions.source] : undefined,
    isLoading,
    shouldHaveRowClickHandler,
    currentColumn,
    messageProps,
    closeMessage,
    showMessage,
    applyColumnFilters,
    handleLazyLoad,
    handleColumnSizeChange,
    handleRowContextMenu,
    isRowSelected,
    handleRowClick,
    closeFilters,
    handleFilter,
    handleSort,
    handleHeaderCellContextMenu,
    getMenuHorizontalPosition,
    closeHeaderActions,
    closeFieldSelector,
    handleColumnAdd,
    handleHeaderContextMenu,
  };
};